<template>
  <div
    class="area-chip"
    :data-area-id="area.id_programa"
    :class="{
      dragging: isDragging,
      'drop-before': isDropTarget && dragState.dropSide === 'before',
      'drop-after':  isDropTarget && dragState.dropSide === 'after',
      seleccionada,
      'en-ruta': enRuta,
      opaca
    }"
    :draggable="true"
    @click.stop="$emit('seleccionar', area)"
    @dragstart.stop="onDragStart"
    @dragover.stop.prevent="onDragOver"
    @dragend="onDragEnd"
    @drop.stop.prevent="onDrop"
  >
    <span class="drag-handle" @click.stop title="Arrastrar">⠿</span>
    <span class="chip-nombre" :title="area.nombre">{{ area.nombre }}</span>
  </div>
</template>

<script lang="ts" setup>
/**
 * Tarjeta de un área del árbol organizacional.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { computed, inject, reactive } from 'vue'
import type { Programa } from '@/interfaces/Programa'

const props = defineProps<{
  area: Programa
  nivel: number
  seleccionada?: boolean
  enRuta?: boolean
  opaca?: boolean
}>()

const emit = defineEmits<{
  (e: 'seleccionar', area: Programa): void
  (e: 'reordenar', payload: { dragged: Programa; target: Programa; side: 'before' | 'after' }): void
}>()

const dragState = inject<{
  dragging: Programa | null
  dropTargetId: number | null
  dropSide: 'before' | 'after'
}>('areaDragState', reactive({ dragging: null, dropTargetId: null, dropSide: 'after' as const }))

const isDragging    = computed(() => dragState.dragging?.id_programa === props.area.id_programa)
const isDropTarget  = computed(() => dragState.dropTargetId === props.area.id_programa)
const isValidDrop   = computed(() =>
  dragState.dragging != null &&
  dragState.dragging.id_programa !== props.area.id_programa &&
  dragState.dragging.padre_id === props.area.padre_id
)

function onDragStart(e: DragEvent) {
  dragState.dragging = props.area
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
}

function onDragOver(e: DragEvent) {
  if (!isValidDrop.value) return
  dragState.dropTargetId = props.area.id_programa!
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  dragState.dropSide = e.clientX < rect.left + rect.width / 2 ? 'before' : 'after'
}

function onDragEnd() {
  dragState.dragging = null
  dragState.dropTargetId = null
}

function onDrop() {
  if (!dragState.dragging || !isValidDrop.value) {
    dragState.dragging = null
    dragState.dropTargetId = null
    return
  }
  const dragged = dragState.dragging
  const side    = dragState.dropSide
  dragState.dragging = null
  dragState.dropTargetId = null
  emit('reordenar', { dragged, target: props.area, side })
}
</script>

<style scoped>
.area-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: 150px;
  flex-shrink: 0;
  background-color: hsl(210, 60%, 92%);
  border: 1px solid #ccdeed;
  border-left: 3px solid #8bb8d6;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  font-family: var(--font-family);
  position: relative;
  z-index: 1;
  transition: box-shadow 0.15s, opacity 0.15s, background-color 0.15s;
  user-select: none;
}

.area-chip:hover {
  background-color: hsl(210, 60%, 88%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.13);
}

.area-chip.seleccionada {
  border: 2px solid #0e69a0;
  border-left: 3px solid #0e69a0;
  background-color: hsl(210, 60%, 87%);
  box-shadow: 0 0 0 2px rgba(14, 105, 160, 0.2);
}

.area-chip.dragging { opacity: 0.35; }

.area-chip.opaca {
  opacity: 0.25;
  pointer-events: none;
}

.area-chip.en-ruta {
  border-left: 3px solid #eea222;
  background-color: hsl(38, 90%, 94%);
  box-shadow: 0 0 0 1px rgba(238, 162, 34, 0.35);
}

.area-chip.drop-before { border-left:  3px solid #0e69a0; }
.area-chip.drop-after  { border-right: 3px solid #0e69a0; }

.drag-handle {
  color: #ccc;
  font-size: 13px;
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
  transition: color 0.15s;
}
.area-chip:hover .drag-handle { color: #0e69a0; }

.chip-nombre {
  flex: 1;
  font-size: 0.8rem;
  font-weight: 600;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
